import {listen} from 'nuffjs';
import {togglerMixin, togglerConf} from './toggler';

/**
 * @property {string} dialogOpenEvent=dialog:open the dialog open event to listen for
 * @property {string} dialogCloseEvent=dialog:close the dialog close event to listen for
 * @extends Toggler.conf
 * @memberof DialogToggler
 */
const conf = Object.assign({}, togglerConf, {
	dialogOpenEvent: 'dialog:open',
	dialogCloseEvent: 'dialog:close',
});

/**
 * create the DialogToggler class with a dynamic super class
 *
 * @mixin
 * @param {typeof HTMLElement} Base the base class
 * @returns {typeof DialogToggler} the created class
 */
const dialogTogglerMixin = (Base) =>
	/**
	 * @classdesc The DialogToggler is a Toggler dedicated to toggle [Dialogs]{@link Dialog}. In
	 * addition to a normal Toggler, it also monitors the Dialog events to update its status in case
	 * the targeted Dialog is being toggled by a different element.
	 *
	 * @class DialogToggler
	 * @extends Toggler
	 * @version 1.0.0
	 * @path
	 *
	 * @listens dialog:open
	 * @listens dialog:close
	 *
	 */
	class extends togglerMixin(Base) {
		static conf = conf;

		firstConnectedCallback() {
			super.firstConnectedCallback();
			this.initDialogListener();
		}

		/**
		 * Restart to listen to the events, in case of reconnection
		 *
		 * @memberof DialogToggler
		 * @instance
		 */
		connectedCallback() {
			this.initDialogListener();
		}

		/**
		 * Stops to listen to the events on disconnection
		 *
		 * @memberof DialogToggler
		 * @instance
		 */
		disconnectedCallback() {
			if (this.dialogListener) {
				this.dialogListener.unlisten();
				this.dialogListener = null;
			}
		}

		/**
		 * Adds the events listeners
		 *
		 * @memberof DialogToggler
		 * @instance
		 * @protected
		 * @ignore
		 */
		initDialogListener() {
			this.dialogListener = listen(
				document,
				[conf.dialogOpenEvent, conf.dialogCloseEvent],
				this.onDialogStatusChange.bind(this)
			);
		}

		/**
		 * React to Dialog events and update its status accordingly
		 *
		 * @param {Event} event the event listend
		 * @memberof DialogToggler
		 * @instance
		 * @async
		 * @protected
		 * @ignore
		 */
		async onDialogStatusChange(event) {
			const targets = await this.getTargets();
			if (targets.includes(event.target)) {
				this.resetToggled(event.target.isOpened());
			}
		}
	};

export {dialogTogglerMixin, conf as dialogTogglerConf};
